import { NSARouterMakerResult } from "namirasoft-account-react";
import { FailoverRow, GatewayRow, LoadBalancerRow, ModelRow, RaceRow, RouterRow } from "namirasoft-inference";
import { NSCard, NSColumn, NSLineText, NSLoading, NSSection, NSSpace, NSSpaceSizeType } from "namirasoft-site-react";
import { Component } from "react";
import { InferenceChatTargetType, InferenceRoutes } from "../InferenceRoutes";
import { InferenceRouterProps } from "../InferenceRouterProps";

const PAGE_SIZE = 20;

interface HomeEntitiesProps extends NSARouterMakerResult, InferenceRouterProps { }

interface HomeEntitiesState
{
    loading: boolean;
    models: ModelRow[];
    load_balancers: LoadBalancerRow[];
    failovers: FailoverRow[];
    races: RaceRow[];
    routers: RouterRow[];
    gateways: GatewayRow[];
}

interface Section<T extends { id: string; name: string }>
{
    title: string;
    rows: T[];
    target_type: InferenceChatTargetType;
    listURL: string;
}

class HomeEntities extends Component<HomeEntitiesProps, HomeEntitiesState>
{
    constructor(props: HomeEntitiesProps)
    {
        super(props);
        this.state = {
            loading: true,
            models: [],
            load_balancers: [],
            failovers: [],
            races: [],
            routers: [],
            gateways: [],
        };
    }

    override componentDidMount(): void
    {
        this.load();
    }

    private async load()
    {
        try
        {
            let [models, load_balancers, failovers, races, routers, gateways] = await Promise.all([
                this.props.servers.inference.model.List([], 1, PAGE_SIZE, [], null),
                this.props.servers.inference.load_balancer.List([], 1, PAGE_SIZE, [], null),
                this.props.servers.inference.failover.List([], 1, PAGE_SIZE, [], null),
                this.props.servers.inference.race.List([], 1, PAGE_SIZE, [], null),
                this.props.servers.inference.router.List([], 1, PAGE_SIZE, [], null),
                this.props.servers.inference.gateway.List([], 1, PAGE_SIZE, [], null),
            ]);
            this.setState({
                loading: false,
                models: models.rows,
                load_balancers: load_balancers.rows,
                failovers: failovers.rows,
                races: races.rows,
                routers: routers.rows,
                gateways: gateways.rows,
            });
        }
        catch
        {
            this.setState({ loading: false });
        }
    }

    private renderSection<T extends { id: string; name: string }>(section: Section<T>, isLast: boolean)
    {
        return (
            <NSColumn key={section.target_type} style={{ gap: 0 }}>
                <NSLineText title={section.title} />
                <NSSpace size={NSSpaceSizeType.SMALL} />
                <NSSection style={{ gap: "24px" }}>
                    {
                        section.rows.map((row) => (
                            <NSCard
                                key={row.id}
                                title={row.name || row.id.slice(-6)}
                                image={{
                                    src: "https://static.namirasoft.com/image/namirasoft/inference/logo/base/192.png",
                                    alt: "Inference",
                                }}
                                link={{
                                    href: InferenceRoutes.ChatNew(section.target_type, row.id),
                                    target: "_self",
                                    children: "Chat Now",
                                }}
                            />
                        ))
                    }
                    <NSCard
                        key={`${section.target_type}_view_all`}
                        title={`View all ${section.title}`}
                        image={{
                            src: "https://static.namirasoft.com/image/namirasoft/inference/logo/base/192.png",
                            alt: section.title,
                        }}
                        link={{
                            href: section.listURL,
                            target: "_self",
                            children: "View All",
                        }}
                    />
                </NSSection>
                {!isLast && <NSSpace size={NSSpaceSizeType.SMALL} />}
            </NSColumn>
        );
    }

    override render()
    {
        if (this.state.loading)
            return <NSLoading title="Loading your inference resources ..." />;

        let sections: Section<any>[] = [
            {
                title: "Gateways",
                rows: this.state.gateways,
                target_type: InferenceChatTargetType.Gateway,
                listURL: InferenceRoutes.GatewayList,
            },
            {
                title: "Routers",
                rows: this.state.routers,
                target_type: InferenceChatTargetType.Router,
                listURL: InferenceRoutes.RouterList,
            },
            {
                title: "Races",
                rows: this.state.races,
                target_type: InferenceChatTargetType.Race,
                listURL: InferenceRoutes.RaceList,
            },
            {
                title: "Failovers",
                rows: this.state.failovers,
                target_type: InferenceChatTargetType.Failover,
                listURL: InferenceRoutes.FailoverList,
            },
            {
                title: "Load Balancers",
                rows: this.state.load_balancers,
                target_type: InferenceChatTargetType.LoadBalancer,
                listURL: InferenceRoutes.LoadBalancerList,
            },
            {
                title: "Models",
                rows: this.state.models,
                target_type: InferenceChatTargetType.Model,
                listURL: InferenceRoutes.ModelList,
            },
        ];

        let visible = sections.filter((s) => s.rows.length > 0);

        return (
            <NSColumn style={{ gap: 0 }}>
                {visible.map((s, i) => this.renderSection(s, i === visible.length - 1))}
            </NSColumn>
        );
    }
}

export default HomeEntities;